import { MessageCircle, Shield, Users, Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';

export default function Hero() {
  const { t } = useLanguage();

  return (
    <section className="relative pt-24 sm:pt-32 md:pt-36 lg:pt-40 pb-16 sm:pb-20 md:pb-24 overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-amber-50">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000a1f' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat'
          }}
        />
      </div>

      <div className="container mx-auto px-4 relative">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          
          {/* Left Content */}
          <div className="order-2 lg:order-1">
            {/* Badge */}
            <div className="inline-block bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              🏥 {t('Since 2018 in Simbock', 'Depuis 2018 à Simbock')}
            </div>
            
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-blue-900 mb-4 sm:mb-6 leading-tight">
              {t('Your Health,', 'Votre Santé,')}
              <span className="text-amber-600 block">
                {t('Our Priority', 'Notre Priorité')}
              </span>
            </h1>
            
            {/* Description */}
            <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 leading-relaxed max-w-2xl">
              {t(
                'Welcome to Clinique Mains Douces Plus, your trusted healthcare center in Tradex Simbock, Yaoundé. We offer quality medical services with gentle hands and a caring heart.',
                'Bienvenue à la Clinique Mains Douces Plus, votre centre de soins de confiance à Tradex Simbock, Yaoundé. Nous offrons des services médicaux de qualité avec des mains douces et un cœur attentionné.'
              )}
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-3 sm:grid-cols-3 gap-3 sm:gap-4 mb-8">
              {[
                { icon: Shield, text: { en: 'Professional Care', fr: 'Soins Pro' } },
                { icon: Users, text: { en: 'Expert Team', fr: 'Équipe Experte' } },
                { icon: Clock, text: { en: '24H/24 7j/7 Service', fr: 'Service 24H/24 7j/7' } }
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="flex items-center gap-2 bg-white/80 backdrop-blur-sm px-3 py-2 rounded-lg shadow-sm">
                    <Icon className="text-blue-600 flex-shrink-0" size={18} />
                    <span className="text-xs sm:text-sm font-medium text-blue-900">
                      {t(item.text.en, item.text.fr)}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg transition transform hover:scale-105 shadow-lg"
              >
                {t('Book an Appointment', 'Prendre Rendez-vous')}
              </Link>
              
              <Link
                to="/services"
                className="inline-flex items-center justify-center bg-white hover:bg-amber-50 text-blue-900 px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg transition transform hover:scale-105 shadow-lg border-2 border-blue-100"
              >
                {t('Our Services', 'Nos Services')}
              </Link>
              
              
            </div>

            {/* Trust Badge */}
            <div className="mt-8 flex items-center gap-4">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 border-2 border-white"></div>
                ))}
              </div>
              <p className="text-sm text-gray-600">
                <span className="font-bold text-blue-900">1000+</span> {t('patients treated', 'patients traités')}
              </p>
            </div>
          </div>

          {/* Right Image Section */}
          <div className="order-1 lg:order-2 relative">
            <div className="relative">
              {/* Main Image */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/about3.png"
                  alt="Clinique Mains Douces Plus - Healthcare Center in Simbock"
                  className="w-full h-auto object-cover aspect-[4/3] lg:aspect-square"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/30 to-transparent"></div>
              </div>

              {/* Floating Card - Experience */}
              <div className="absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-6 bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 animate-float">
                <p className="text-2xl sm:text-3xl font-bold text-blue-900">8+</p>
                <p className="text-xs sm:text-sm text-gray-600">
                  {t('Years of Excellence', 'Années d\'Excellence')}
                </p>
              </div>

              {/* Floating Card - Emergency */}
              <div className="absolute -top-4 -right-4 sm:-top-6 sm:-right-6 bg-amber-500 rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 text-white animate-float-delayed">
                <Clock size={24} className="mb-2" />
                <p className="text-sm sm:text-base font-bold">24H/24 7j/7</p>
              </div>
            </div>

            {/* Location Badge */}
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-blue-900">Tradex Simbock, Yaoundé</span>
            </div>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float 3s ease-in-out infinite;
          animation-delay: 1.5s;
        }
      `}</style>
    </section>
  );
}